import { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useMutation, useQuery } from "@apollo/client";
import type { DocumentNode } from "@apollo/client";
import Button from "@/components/Button";
import Select from "@/components/Select";
import TextField from "@/components/TextField";
import Dialog from "@/components/Dialog";
import type { PersonHit } from "@/components/AlgoliaSearch";
import { NotePriorityValues } from "@/types";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { GovernanceRolePersonPicker } from "@/pages/facilities/[facilityId]/people/governance/_GovernanceRolePersonPicker";
import {
  buildGovernanceTitleOptions,
  getGovernanceTitleFieldState,
  GOVERNANCE_ROLE_NOTE_TITLE
} from "@/pages/facilities/[facilityId]/people/governance/_utils";
import { CREATE_GOVERNANCE_ROLE_MUTATION } from "./sectionGovernanceMutations";
import { GOVERNANCE_TITLES_QUERY, type SectionGovernanceTargetType } from "./sectionGovernanceQueries";

interface AddSectionGovernanceRoleModalProps {
  open: boolean;
  sectionId: string;
  targetType: SectionGovernanceTargetType;
  onClose: () => void;
  refetchQueries?: DocumentNode[];
}

interface FormValues {
  personId: string;
  governanceTitleId: string;
  internalNote: string;
}

export function AddSectionGovernanceRoleModal({
  open,
  sectionId,
  targetType,
  onClose,
  refetchQueries = []
}: AddSectionGovernanceRoleModalProps) {
  const [personSearchKey, setPersonSearchKey] = useState(0);
  const [selectedPerson, setSelectedPerson] = useState<PersonHit | null>(null);
  const [createGovernanceRole, { loading: saving }] = useMutation(CREATE_GOVERNANCE_ROLE_MUTATION, {
    refetchQueries
  });
  const {
    data: titlesData,
    loading: titlesLoading,
    error: titlesError
  } = useQuery(GOVERNANCE_TITLES_QUERY, {
    variables: { targetType },
    fetchPolicy: "cache-first",
    skip: !open
  });
  const governanceTitleOptions = useMemo(
    () => buildGovernanceTitleOptions(titlesData?.governanceTitles),
    [titlesData?.governanceTitles]
  );
  const titleFieldState = getGovernanceTitleFieldState({
    loading: titlesLoading,
    hasError: Boolean(titlesError),
    optionCount: governanceTitleOptions.length
  });

  const { control, handleSubmit, reset, setValue, watch } = useForm<FormValues>({
    defaultValues: {
      personId: "",
      governanceTitleId: "",
      internalNote: ""
    }
  });

  const selectedPersonId = watch("personId");
  const selectedGovernanceTitleId = watch("governanceTitleId");

  useEffect(() => {
    if (!selectedGovernanceTitleId && governanceTitleOptions.length > 0) {
      setValue("governanceTitleId", governanceTitleOptions[0].value);
    }
  }, [governanceTitleOptions, selectedGovernanceTitleId, setValue]);

  const handleClose = () => {
    reset();
    setSelectedPerson(null);
    setPersonSearchKey((key) => key + 1);
    onClose();
  };

  const onSubmit = async (values: FormValues) => {
    const content = values.internalNote.trim();

    try {
      const { data } = await createGovernanceRole({
        variables: {
          input: {
            personId: values.personId,
            governableId: sectionId,
            governableType: targetType,
            governanceTitleId: values.governanceTitleId,
            note: content ? { content, priority: NotePriorityValues.NONE, title: GOVERNANCE_ROLE_NOTE_TITLE } : null
          }
        }
      });

      if (!data?.createGovernanceRole.success) {
        showErrorSnackbar(data?.createGovernanceRole.message || "Unable to add governance role.");
        return;
      }

      showSnackbar("Governance role added");
      handleClose();
    } catch {
      showErrorSnackbar("Unable to add governance role.");
    }
  };

  if (!open) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClosed={handleClose}
      headline="Add Governance Role"
      className="w-[640px]"
      hasFormElements
      displayOverflow
      actions={
        <>
          <Button variant="text" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={!selectedPersonId || !selectedGovernanceTitleId}
            loading={saving}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        <p className="text-body-medium text-sys-on-surface">
          {targetType === "NATIONAL"
            ? "Search for a person and assign a governance role at the National Section."
            : "Search for a person and assign a governance role at this section."}
        </p>

        <Controller
          name="personId"
          control={control}
          rules={{ required: "Person is required" }}
          render={({ fieldState }) => (
            <GovernanceRolePersonPicker
              key={personSearchKey}
              selectedPerson={selectedPerson}
              disabled={saving}
              error={fieldState.invalid}
              errorText={fieldState.error?.message}
              onSelect={(hit) => {
                setSelectedPerson(hit);
                setValue("personId", hit.objectID, { shouldValidate: true });
              }}
              onClear={() => {
                setSelectedPerson(null);
                setValue("personId", "", { shouldValidate: true });
              }}
            />
          )}
        />

        <Controller
          name="governanceTitleId"
          control={control}
          rules={{ required: "Role is required" }}
          render={({ field: { onChange, value }, fieldState }) => (
            <Select
              label="Role"
              options={governanceTitleOptions}
              value={value}
              onSelect={onChange}
              disabled={titleFieldState.disabled}
              error={fieldState.invalid || Boolean(titleFieldState.errorText)}
              errorText={titleFieldState.errorText ?? fieldState.error?.message}
              supportingText={titleFieldState.supportingText}
              required
            />
          )}
        />

        <Controller
          name="internalNote"
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextField
              type="textarea"
              rows={3}
              label="Internal Note (Optional)"
              value={value}
              onChangeText={onChange}
              className="w-full"
            />
          )}
        />
      </div>
    </Dialog>
  );
}
